import React, { useCallback, useEffect, useMemo, useState } from 'react';
export interface ISidebarContext {
  isSearchVisible: boolean;
  isSidebarToggled: boolean;
  isVisible?: boolean;
  toggleSearch: (newValue?: boolean) => void;
  toggleSidebar: (newValue?: boolean, device?: 'mobile') => void;
}
interface SidebarProps {
  children: React.ReactNode;
}
export const SidebarContext = React.createContext<ISidebarContext>({
  toggleSidebar: () => {
    /* noop */
  },
  toggleSearch: () => {
    /* noop */
  },
  isSidebarToggled: false,
  isSearchVisible: false,
  isVisible: undefined
});
SidebarContext.displayName = 'SidebarContext';
const desktop = '(min-width: 992px)';
const tablet = '(min-width: 600px)';
const breakpointsToToggle = [desktop, tablet];
export const SidebarProvider: React.FC<SidebarProps> = ({
  children
}) => {
  const [isToggled, setIsToggled] = useState<boolean>(false);
  const [isSearchVisible, setIsSearchVisible] = useState<boolean>(false);
  const [isVisible, setIsVisible] = useState<boolean | undefined>();
  const resetSidebar = useCallback(() => {
    setIsToggled(false);
    setIsSearchVisible(false);
  }, []);
  const setVisibility = useCallback(() => {
    if (window.innerWidth >= 600) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }, []);
  useEffect(() => {
    if (typeof isVisible === 'undefined') {
      setVisibility();
    }
  }, [isVisible, setVisibility]);

  // Add event listeners to reset sidebar on resize.
  useEffect(() => {
    const mediaQueryLists = breakpointsToToggle.map(breakpoint => window.matchMedia(breakpoint));
    for (const mediaQueryList of mediaQueryLists) {
      mediaQueryList.addEventListener('change', resetSidebar);
    }
    window.addEventListener('resize', setVisibility);
    return () => {
      for (const mediaQueryList of mediaQueryLists) {
        mediaQueryList.removeEventListener('change', resetSidebar);
      }
      window.removeEventListener('resize', setVisibility);
    };
  }, [setVisibility, resetSidebar]);

  // Update body className based on sidebar state
  useEffect(() => {
    const body = document.body;
    if (isToggled) {
      body.classList.add('sidebar-toggled');
    } else {
      body.classList.remove('sidebar-toggled');
    }
    if (isSearchVisible) {
      body.classList.add('search-visible');
    } else {
      body.classList.remove('search-visible');
    }
    if (isVisible === true) {
      body.classList.add('sidebar-visible');
    } else if (isVisible === false) {
      body.classList.remove('sidebar-visible');
    }
  }, [isToggled, isSearchVisible, isVisible]);
  const ctx = useMemo((): ISidebarContext => ({
    toggleSidebar: (newValue, device) => {
      if (device === 'mobile' && window.matchMedia(tablet).matches) {
        return;
      }
      const val = newValue ?? !isToggled;
      if (!(val || window.matchMedia(tablet).matches)) {
        setIsSearchVisible(false);
      }
      setIsToggled(val);
    },
    toggleSearch: (newValue?: boolean) => {
      // If the user on mobile, we want to toggle the sidebar with the search.
      if (!window.matchMedia(tablet).matches) {
        setIsToggled(newValue ?? !isSearchVisible);
      }
      setIsSearchVisible(newValue ?? !isSearchVisible);
    },
    isSearchVisible,
    isSidebarToggled: isToggled,
    isVisible
  }), [isToggled, isSearchVisible, isVisible]);
  return <SidebarContext.Provider value={ctx} data-sentry-element="SidebarContext.Provider" data-sentry-component="SidebarProvider" data-sentry-source-file="index.tsx">{children}</SidebarContext.Provider>;
};